import { ALL_TIERS, canIssueUniqueId, type UserTier } from "@virtuous/shared";

const TOKEN_KEY = "virtuous_session";
const USER_KEY = "virtuous_user";
/** Same-account UI view (industry / tier preview) — does not change server auth */
const VIEW_KEY = "virtuous_view";

export type StoredUser = {
  fullName: string;
  industry: string;
  email?: string;
  uniqueId?: string;
  tier?: number;
  canIssueIds?: boolean;
  /** Booking link on Virtuous profile (for receiving referrals) */
  calendarLink?: string;
  /** Default property / service location */
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
};

/** Client-only view prefs on top of the real account */
export type ViewPrefs = {
  /** Industry home to show (real_estate, finance, …) */
  industry?: string;
  /**
   * UI tier preview for menus / labels.
   * Does NOT raise real API privileges (Earn, security still use real tier).
   */
  tier?: number;
};

export function saveSession(token: string, user: StoredUser) {
  if (typeof window === "undefined") return;
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession() {
  if (typeof window === "undefined") return;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(VIEW_KEY);
}

export function getSessionToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}

/** Real signed-in account (authoritative for API / tier gates). */
export function getStoredUser(): StoredUser | null {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredUser;
  } catch {
    return null;
  }
}

export function getViewPrefs(): ViewPrefs {
  if (typeof window === "undefined") return {};
  const raw = localStorage.getItem(VIEW_KEY);
  if (!raw) return {};
  try {
    return JSON.parse(raw) as ViewPrefs;
  } catch {
    return {};
  }
}

/**
 * Merge view prefs. Pass `null` for industry or tier to clear that override.
 */
export function setViewPrefs(prefs: {
  industry?: string | null;
  tier?: number | null;
}) {
  if (typeof window === "undefined") return;
  const next: ViewPrefs = { ...getViewPrefs() };
  if (prefs.industry === null) {
    delete next.industry;
  } else if (prefs.industry !== undefined && prefs.industry) {
    next.industry = prefs.industry;
  }
  if (prefs.tier === null) {
    delete next.tier;
  } else if (prefs.tier !== undefined && !Number.isNaN(Number(prefs.tier))) {
    next.tier = Number(prefs.tier);
  }
  if (!next.industry && next.tier == null) {
    localStorage.removeItem(VIEW_KEY);
  } else {
    localStorage.setItem(VIEW_KEY, JSON.stringify(next));
  }
  window.dispatchEvent(new CustomEvent("virtuous:view-change"));
}

export function clearViewPrefs() {
  if (typeof window === "undefined") return;
  localStorage.removeItem(VIEW_KEY);
  window.dispatchEvent(new CustomEvent("virtuous:view-change"));
}

export function hasViewOverride(): boolean {
  const v = getViewPrefs();
  const u = getStoredUser();
  if (!u) return false;
  if (v.industry && v.industry !== u.industry) return true;
  if (v.tier != null && Number(v.tier) !== Number(u.tier)) return true;
  return false;
}

/**
 * Account + view prefs for UI (homes, labels, optional tier preview).
 * Security-sensitive routes must use getStoredUser() / real tier instead.
 */
export function getEffectiveUser(): StoredUser | null {
  const u = getStoredUser();
  if (!u) return null;
  const v = getViewPrefs();
  const industry = v.industry || u.industry;
  const tier =
    v.tier != null && ALL_TIERS.includes(Number(v.tier) as UserTier)
      ? Number(v.tier)
      : u.tier;
  const canIssueIds =
    tier != null
      ? canIssueUniqueId(Number(tier) as UserTier)
      : u.canIssueIds;
  return {
    ...u,
    industry,
    tier,
    canIssueIds,
  };
}

/** Industry home path for the effective (view) industry. */
export function getHomePath(user?: StoredUser | null): string {
  const u = user ?? getEffectiveUser() ?? getStoredUser();
  if (!u?.industry) return "/enter";
  return `/home/${u.industry}`;
}

/** Real account is Tier 1 (server-backed ops: Earn, security, source leads). */
export function isRealTier1(): boolean {
  const u = getStoredUser();
  return Boolean(u && Number(u.tier) === 1);
}

/**
 * Real account is network leadership: Tier 1 or Tier 1.5.
 * View-mode preview cannot unlock leadership gates.
 */
export function isRealNetworkLeadership(): boolean {
  const u = getStoredUser();
  if (!u) return false;
  const t = Number(u.tier);
  return t === 1 || t === 1.5;
}

/**
 * Product rule: only Tier 1 and 1.5 may browse Virtuous **source code**
 * (`/source-code`, `/api/source-code/*`).
 */
export function canViewSourceCodeClient(): boolean {
  return isRealNetworkLeadership();
}

/** @deprecated use canViewSourceCodeClient */
export function canViewSystemCodeClient(): boolean {
  return canViewSourceCodeClient();
}

/** Password vault — Tier 1 only */
export function canReadSensitiveFilesClient(): boolean {
  return isRealTier1();
}

/**
 * Partner lead sources (Zbuyer, Prometheus, MarketTime, etc.) are Tier 1 only.
 * Anyone under Tier 2 (i.e. Tier 2+) must not see partner branding —
 * those leads rebrand as Virtuous on the network.
 */
export function canSeePartnerLeadSources(): boolean {
  return isRealTier1();
}

/** Public origin label: partner sources → Virtuous for non–Tier-1. */
export function publicLeadSourceLabel(
  source: string | null | undefined,
): string {
  if (canSeePartnerLeadSources()) {
    return source?.trim() || "Virtuous";
  }
  if (!source) return "Virtuous";
  const s = source.trim().toLowerCase();
  if (
    s.startsWith("partner:") ||
    /zbuyer|prometheus|markettime|myreferral|my.?referral/.test(s)
  ) {
    return "Virtuous";
  }
  return source.trim() || "Virtuous";
}

/** Backend usage tracking — Unique ID is for tracking, not login */
export async function trackPage(path: string, eventType = "page_view") {
  if (typeof window === "undefined") return;
  const token = getSessionToken();
  const user = getStoredUser();
  if (!token && !user?.uniqueId) return;
  try {
    // Same-origin on phone/PC (Next rewrites /api → backend)
    const base =
      typeof window !== "undefined"
        ? ""
        : (process.env.NEXT_PUBLIC_API_URL ?? "http://127.0.0.1:4000").replace(
            /\/$/,
            "",
          );
    await fetch(`${base}/api/access/track`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        eventType,
        path,
        uniqueId: user?.uniqueId,
      }),
    });
  } catch {
    /* non-fatal */
  }
}
